<!-- eslint-disable vue/v-on-event-hyphenation -->
<template>
  <div class="wrapper-widget">
    <WidgetGrid v-bind="propsForWidgetGrid" />
    <!-- Floating footer with animation -->
    <UserConfirmedActions
      v-if="showUserConfirmedActions"
      :show="showUserConfirmedActions"
      :data="data"
    />
    <UserResponseError
      v-if="userResponsesError"
      :userResponsesError="userResponsesError"
    />
    <UserShouldConfirmActions
      v-if="userHasActionsThatNeedConfirmation"
      :data="data"
      :userHasActionsThatNeedConfirmation="userHasActionsThatNeedConfirmation"
      :userActionsThatNeedConfirmation="userActionsThatNeedConfirmation"
      @confirmActions="confirmActions"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
// import WidgetGrid from '@/components/results/subcomponents/WidgetGrid.vue'
import WidgetBody from '@/components/widgets/shared/WidgetBody.vue'
import WidgetHeader from '@/components/widgets/shared/WidgetHeader.vue'
import UserConfirmedActions from './subcomponents/UserConfirmedActions.vue'
import UserResponseError from './subcomponents/UserResponseError.vue'
import UserShouldConfirmActions from './subcomponents/UserShouldConfirmActions.vue'

export const wrapperWidgetInformation = ({
  colData,
  userIsParticipatingInSurvey
}) => {
  const requests = {
    [requestTypes.TAGS]: {},
    [requestTypes.SURVEY_DETAILS]: {},
    updateResponses: {}
  }
  if (userIsParticipatingInSurvey) {
    requests[requestTypes.RESPONSES] = {}
  }
  return {
    requests
  }
}

export const wrapperWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 2 }]
      }
    ]
  }
}

const WrapperWidget = {
  name: 'WrapperWidget',
  components: {
    WidgetGrid: defineAsyncComponent(
      () => import('@/components/results/subcomponents/WidgetGrid.vue')
    ),
    WidgetBody,
    WidgetHeader,
    UserConfirmedActions,
    UserResponseError,
    UserShouldConfirmActions
  },
  mixins: [commonWidgetProps],
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    gridProps: {
      type: Object,
      default: () => {}
    },
    responses: {
      type: Array,
      default: () => []
    },
    updateResponses: {
      type: Function,
      default: () => {}
    }
  },
  computed: {
    userActionsThatNeedConfirmation() {
      return this.userResponses
        .filter((r) => r.actions.every((a) => !a.done))
        .reduce((actions, r) => {
          actions.push(...(r.actions || []))
          return actions
        }, [])
    },
    userHasNoSelectedActions() {
      return this.userResponses.every((r) => r.actions.length === 0)
    },
    userHasActionsThatNeedConfirmation() {
      return (
        this.data.variant === 'friskkollen' &&
        this.surveyDetails?.details?.status === 'published' &&
        this.userActionsThatNeedConfirmation.length > 0
      )
    },
    userResponses() {
      return this.responses
    },
    userResponsesError() {
      return false
      // return this.$store.getters.userResponsesError
    },
    userResponsesLoading() {
      return false
      // return this.$store.getters.userResponsesLoading
    },
    userHasConfirmedActions() {
      return (
        this.data.variant === 'friskkollen' &&
        this.userResponses.some((r) => r.actions.length) &&
        this.userResponses.every(
          (r) => !r.actions.length || r.actions.every((a) => a.done)
        )
      )
    },
    userIsAdminForCurrentGroupOrSuperAdmin() {
      return this.userIsGroupAdmin || this.userIsPulsAdmin
    },
    propsForWidgetGrid() {
      return {
        ...this.gridProps,
        resultViewForUser: {
          ...this.gridProps.resultView,
          rows: this.data.rows
        },
        userCanSelectAlertActions:
          this.userHasActionsThatNeedConfirmation ||
          this.userHasNoSelectedActions,
        userHasConfirmedActions: this.userHasConfirmedActions
      }
    },
    showUserConfirmedActions() {
      return (
        this.userHasConfirmedActions &&
        !this.userResponsesError &&
        !this.userResponsesLoading
      )
    }
  },
  methods: {
    confirmActions() {
      const responsesWithoutConfirmation = this.userResponses.filter(
        (r) => r.actions.length && r.actions.some((a) => !a.done)
      )
      let responses = []
      for (const response of responsesWithoutConfirmation) {
        const groups = // workaround since groups can be stored as string or object...
          typeof response.groups[0] === 'object'
            ? response.groups
            : response.groups.map((name) => {
                return { name, access: 'write' }
              })
        const params = {
          questions: response.questions,
          actions: response.actions.map((r) => {
            return {
              ...r,
              done: true
            }
          }),
          resultRule: response.resultRule,
          groups,
          order: response.order
        }
        responses.push({
          categoryName: response.categoryName,
          params
        })
      }
      // const surveyName = this.surveyName
      this.updateResponses({
        responses,
        confirm: true
      })
      // this.updateResponses({
      //   action,
      //   categoryName: categoryResponse.categoryName,
      //   params
      // })
      // this.$store.dispatch('confirmActions', { surveyName })
    }
  }
}

export default WrapperWidget
</script>

<style lang="scss">
.friskkollen-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--kt-primary-light) !important;
  color: var(--kt-primary-active) !important;
  padding: 10px 0;
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 0.3);
  z-index: 1000; /* Ensure the footer is on top */
}
.friskkollen-success-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--kt-success-light) !important;
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 0.3);
  z-index: 1000; /* Ensure the footer is on top */
}

.friskkollen-error-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--kt-danger-light) !important;
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 0.3);
  z-index: 1000; /* Ensure the footer is on top */
}

.slide-up-down-enter-active,
.slide-up-down-leave-active {
  transition: transform 0.1s ease-in-out;
}

.slide-up-down-enter {
  transform: translateY(100%);
}

.slide-up-down-leave-to {
  transform: translateY(100%);
}

.close-wrapper-alert-button {
  /* position to the right */
  position: absolute;
  right: 0;
  top: 0;
  /* padding */
  padding: 10px;
}
</style>
